import React, { useState, useEffect } from "react";
import {
    Table,
    Card,
    Button,
    Modal,
    Form,
    Input,
    message,
    Spin,
    Select,
} from "antd";
import {
    PlusOutlined,
    SearchOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import useApis from "../apis/useApis";
import appConstant from "../json/appConstant.json";

const PropertyCommercialCategories = ({ isDarkMode }) => {
    const [form] = Form.useForm();
    const [categories, setCategories] = useState([]);
    const [propertyCategories, setPropertyCategories] = useState([]); // new state
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [isDataLoading, setIsDataLoading] = useState(false);

    const { jsonApi } = useApis();

    useEffect(() => {
        fetchCategories(pagination.current, pagination.pageSize, search);
        fetchPropertyCategories(); // Fetch property categories
    }, [pagination.current, pagination.pageSize, search]);

    const fetchCategories = async (page, pageSize, search = "") => {
        setIsDataLoading(true);
        const params = {
            page,
            pageSize,
            search,
        };

        const response = await jsonApi(appConstant.PropertyStatus, params, {}, null);

        if (response?.data?.code === 200 && response?.data?.status === "success") {
            const data = response.data.data.categories || response.data.data;
            setCategories(
                data.map((cat) => ({
                    key: cat.Id,
                    id: cat.Id,
                    PStatusDetails: cat.PStatusDetails,
                    PStatusDetailsH: cat.PStatusDetailsH,
                    PCategoryID: cat.property_categorie.name,
                    isActive: cat.IsActive,
                }))
            );
            setPagination((prev) => ({
                ...prev,
                total: response.data.data.total || data.length,
            }));
        }
        setIsDataLoading(false);
    };

    const fetchPropertyCategories = async () => {
        try {
            const response = await jsonApi(appConstant.PropertyCategory, null, {}, null);
            if (response?.data?.code === 200 && response?.data?.status === "success") {
                const data = response.data.data.categories || response.data.data;
                setPropertyCategories(data);
            }
        } catch (error) {
            console.error("Error fetching property categories:", error);
            message.error("Failed to load property categories");
        }
    };

    const handleAddCategory = async (values) => {
        setLoading(true);
        try {
            const payload = {
                PStatusDetails: values.PStatusDetails,
                PStatusDetailsH: values.PStatusDetailsH,
                PCategoryID: values.pCategoryId,
                IsActive: true,
            };

            const response = await jsonApi(
                appConstant.createPropertyStatus,
                null,
                payload,
                null
            );

            if (response.data.code === 200) {
                message.success("Commercial category added successfully");
                fetchCategories(pagination.current, pagination.pageSize);
                setIsModalVisible(false);
                form.resetFields();
            } else {
                message.error(response.message || "Failed to add category");
            }
        } catch (error) {
            console.error("Add Commercial Category Error:", error);
            message.error("An error occurred");
        }
        setLoading(false);
    };

    const columns = [
        {
            title: "S.No",
            key: "serialNumber",
            render: (_, __, index) =>
                (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Property Status Details",
            dataIndex: "PStatusDetails",
            key: "PStatusDetails",
            sorter: (a, b) => a.PStatusDetails.localeCompare(b.PStatusDetails),
        },
        {
            title: "Property Status Details Hindi",
            dataIndex: "PStatusDetailsH",
            key: "PStatusDetailsH",
            sorter: (a, b) => a.PStatusDetailsH.localeCompare(b.PStatusDetails),
        },
        {
            title: "Property Category",
            dataIndex: "PCategoryID",
            key: "PCategoryID",

        },
        {
            title: "Active",
            dataIndex: "isActive",
            key: "isActive",
            render: (active) => (active ? "Yes" : "No"),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 16,
                }}
            >
                <h2 style={{ color: isDarkMode ? "#fff" : "#000" }}>
                    Property Status
                </h2>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsModalVisible(true)}
                >
                    Add Property Status
                </Button>
            </div>

            <Input
                placeholder="Search status..."
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                suffix={
                    search && (
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setSearch("");
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                        />
                    )
                }
                style={{ width: 300, marginBottom: 16 }}
            />

            <Card
                style={{
                    background: isDarkMode ? "#1f1f1f" : "#fff",
                }}
            >
                <Spin spinning={isDataLoading}>
                    <Table
                        columns={columns}
                        dataSource={categories}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50"],
                            onChange: (page, pageSize) => {
                                setPagination({ current: page, pageSize });
                            },
                        }}
                        rowKey="id"
                    />
                </Spin>
            </Card>

            <Modal
                visible={isModalVisible}
                title="Add Commercial Category"
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setIsModalVisible(false);
                            form.resetFields();
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        onClick={() => form.submit()}
                    >
                        Add
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical" onFinish={handleAddCategory}>
                    <Form.Item
                        name="pCategoryId"
                        label="Property Category"
                        rules={[{ required: true, message: "Please select a property category" }]}
                    >
                        <Select placeholder="Select property category">
                            {propertyCategories.map((category) => (
                                <Select.Option key={category.id} value={category.id}>
                                    {category.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="PStatusDetails"
                        label="Property Status Detail"
                        rules={[
                            { required: true, message: "Please enter a property status" },
                            { max: 250, message: "Maximum 250 characters allowed" },
                            {
                                pattern: /^[A-Za-z\s]+$/,
                                message: "Only alphabets and spaces are allowed",
                            },
                        ]}
                    >
                        <Input.TextArea placeholder="Enter property status" rows={4} />
                    </Form.Item>

                    <Form.Item
                        name="PStatusDetailsH"
                        label="Property Status Detail Hindi"
                        rules={[
                            { required: true, message: "Please enter property status" },
                            { max: 250, message: "Maximum 250 characters allowed" },
                            
                        ]}
                    >
                        <Input placeholder="Enter property status" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PropertyCommercialCategories;
